import type { Cell, CellStyle, Graph } from '@maxgraph/core';

export interface EdgeNormalizationResult {
  inspectedEdgeCount: number;
  normalizedEdgeCount: number;
  clearedGeometryPointCount: number;
  curvedStyleResetCount: number;
  edgeStyleResetCount: number;
  normalizedEdgeIds: string[];
}

function parseStyle(style: string | CellStyle | null | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!style) return result;

  if (typeof style === 'object') {
    for (const [key, value] of Object.entries(style)) {
      if (value == null) continue;
      result[key] = String(value);
    }
    return result;
  }

  for (const token of style.split(';')) {
    if (!token) continue;
    const [key, value] = token.split('=');
    if (!key) continue;
    result[key] = value ?? '';
  }

  return result;
}

function isGroupCell(cell: Cell): boolean {
  const value = cell.getValue();
  return value != null && typeof value === 'object' && value.kind === 'group';
}

function collectLayoutManagedEdges(graph: Graph, parent: Cell): Cell[] {
  const edges: Cell[] = [];
  const seen = new Set<string>();

  const visit = (container: Cell): void => {
    const directEdges = graph.getChildCells(container, false, true);
    for (const edge of directEdges) {
      const id = edge.getId();
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      edges.push(edge);
    }

    const vertices = graph.getChildCells(container, true, false);
    for (const vertex of vertices) {
      if (isGroupCell(vertex)) {
        visit(vertex);
      }
    }
  };

  visit(parent);
  return edges;
}

/**
 * Enforces straight-edge rendering invariants by clearing geometry control
 * points and resetting style keys that can reintroduce curves.
 */
export function normalizeLayoutManagedEdges(graph: Graph, parent: Cell): EdgeNormalizationResult {
  const result: EdgeNormalizationResult = {
    inspectedEdgeCount: 0,
    normalizedEdgeCount: 0,
    clearedGeometryPointCount: 0,
    curvedStyleResetCount: 0,
    edgeStyleResetCount: 0,
    normalizedEdgeIds: [],
  };

  const edges = collectLayoutManagedEdges(graph, parent);
  const model = graph.getDataModel();

  for (const edge of edges) {
    if (!edge.isEdge()) continue;

    result.inspectedEdgeCount += 1;

    let changed = false;
    const edgeId = edge.getId();

    const geometry = edge.getGeometry()?.clone();
    const pointCount = geometry?.points?.length ?? 0;
    if (geometry && pointCount > 0) {
      result.clearedGeometryPointCount += pointCount;
      geometry.points = [];
      model.setGeometry(edge, geometry);
      changed = true;
    }

    const style = parseStyle(edge.getStyle());
    const curvedValue = (style.curved ?? '').toLowerCase();
    const edgeStyleValue = style.edgeStyle ?? '';
    const hasExplicitEdgeStyle = Object.prototype.hasOwnProperty.call(style, 'edgeStyle');

    const shouldResetCurved = curvedValue === '1' || curvedValue === 'true';
    const shouldResetEdgeStyle = hasExplicitEdgeStyle && edgeStyleValue !== 'none';

    if (shouldResetCurved) {
      graph.setCellStyles('curved', '0', [edge]);
      result.curvedStyleResetCount += 1;
      changed = true;
    }

    if (shouldResetEdgeStyle) {
      graph.setCellStyles('edgeStyle', 'none', [edge]);
      result.edgeStyleResetCount += 1;
      changed = true;
    }

    if (changed) {
      result.normalizedEdgeCount += 1;
      if (edgeId) result.normalizedEdgeIds.push(edgeId);
    }
  }

  return result;
}
